/**
 * The route table, read by the header nav AND by the page headers.
 *
 * One table for the same reason `downwind-console` has one: two surfaces that
 * name the same screen independently will eventually name it two different
 * things, and the one that is wrong is always the one the customer read first.
 *
 * `code` is the mono micro-cap pair the Briefing Deck prints above every page
 * title. In the console it is an operator's shorthand — something to write on a
 * ticket. Here it is quieter than that, and it is doing a different job: it is
 * the piece of furniture that makes this page look like the same document as
 * the app the customer bought, rather than like a generic account page bolted
 * to the side of it.
 */

export type NavItem = {
  to: string;
  /** The eyebrow's left half. Three or four characters. */
  code: string;
  label: string;
  /**
   * Shown in the header nav. `/account` is reachable from the identity control
   * instead, because a nav with four items where one of them is "Account" reads
   * as three items and a settings link anyway.
   */
  inHeader: boolean;
};

export const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { to: "/", code: "LIC", label: "Your licenses", inHeader: true },
  { to: "/claim", code: "ADD", label: "Add a key", inHeader: true },
  { to: "/support", code: "SUP", label: "Support", inHeader: true },
  { to: "/account", code: "ACC", label: "Account", inHeader: false },
];

export const HEADER_NAV = NAV_ITEMS.filter((item) => item.inHeader);

/**
 * The item a path belongs to.
 *
 * Longest match wins, and `/` is only ever an exact match. A naive
 * `startsWith` would light every row for the overview, and — the failure the
 * console's test pins — would light `/claim` for a hypothetical
 * `/claims-history`, so the boundary is checked rather than the prefix.
 */
export function activeNavItem(pathname: string): NavItem | undefined {
  if (pathname === "/") return NAV_ITEMS[0];

  let best: NavItem | undefined;
  for (const item of NAV_ITEMS) {
    if (item.to === "/") continue;
    if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
      if (!best || item.to.length > best.to.length) best = item;
    }
  }

  // A license detail page is filed under the overview: it is the screen you
  // reach by clicking a license, and the eyebrow saying LIC is what tells the
  // customer they have not left their licenses.
  if (!best && pathname.startsWith("/licenses/")) return NAV_ITEMS[0];
  return best;
}
